import { useCallback, useEffect, useMemo, useRef } from "react";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import type { Cartridge } from "../models/Cartridge";
import { activeColumnDef } from "@/controls/ag-grid/components/ActiveColumn";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";

interface CartridgesTableProps {
	cartridges: Cartridge[];
	selectedCartridge: Cartridge | null;
	onRowSelected: (cartridge: Cartridge | null) => void;
	onDelete: (cartridge: Cartridge) => void;
}

// Cartridge and mill size names are numbers kept as text ("1", "10", "340"), so a
// plain string sort would put "10" before "2".
const numericAwareComparator = (a: string | null, b: string | null) =>
	(a ?? "").localeCompare(b ?? "", undefined, { numeric: true });

export const CartridgesTable = ({
	cartridges,
	selectedCartridge,
	onRowSelected,
	onDelete,
}: CartridgesTableProps) => {
	const gridRef = useRef<AgGridReact<Cartridge>>(null);
	const { defaultColDef } = useGridDefinitions({});

	// Clear grid selection when selectedCartridge has no Id (new item or deselected)
	useEffect(() => {
		if (!selectedCartridge?.Id && gridRef.current?.api) {
			gridRef.current.api.deselectAll();
		}
	}, [selectedCartridge]);

	// getRowClass isn't re-run on data updates, so a re-activated row keeps its
	// stale "ag-row-inactive" (grey) class. Redraw rows when the data changes.
	useEffect(() => {
		gridRef.current?.api?.redrawRows();
	}, [cartridges]);

	const sortableDefaultColDef = useMemo<ColDef>(
		() => ({ ...defaultColDef, sortable: true }),
		[defaultColDef],
	);

	const columnDefs = useMemo<ColDef<Cartridge>[]>(
		() => [
			{
				field: "Name",
				headerName: "Name",
				flex: 1,
				minWidth: 120,
				// initialSort, not sort: a recreated columnDefs must not reset the user sort.
				initialSort: "asc",
				comparator: numericAwareComparator,
			},
			{
				field: "MillSizeName",
				headerName: "Mill Size",
				width: 100,
				comparator: numericAwareComparator,
			},
			{
				field: "Description",
				headerName: "Description",
				flex: 2,
				minWidth: 150,
			},
			activeColumnDef<Cartridge>(),
			{
				headerName: "",
				width: 50,
				minWidth: 50,
				maxWidth: 50,
				sortable: false,
				filter: false,
				resizable: false,
				cellRenderer: (params: { data: Cartridge }) => (
					<IconButton
						size="small"
						color="error"
						onClick={(e) => {
							e.stopPropagation();
							if (params.data) {
								onDelete(params.data);
							}
						}}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				),
			},
		],
		[onDelete],
	);

	const handleRowClicked = useCallback(
		(event: RowClickedEvent<Cartridge>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<Cartridge>(onRowSelected);

	const getRowClass = useCallback(
		(params: { data: Cartridge | undefined }) => {
			const classes: string[] = [];
			if (params.data && selectedCartridge?.Id === params.data.Id) {
				classes.push("ag-row-selected");
			}
			if (params.data && !params.data.Active) {
				classes.push("ag-row-inactive");
			}
			return classes.join(" ");
		},
		[selectedCartridge],
	);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<Cartridge>
				ref={gridRef}
				rowData={cartridges}
				columnDefs={columnDefs}
				defaultColDef={listColDef(sortableDefaultColDef)}
				enableBrowserTooltips
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				animateRows
				onRowClicked={handleRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={(params) => `${params.data.Id}`}
				getRowClass={getRowClass}
				gridOptions={{
					rowSelection: {
						mode: "singleRow",
						checkboxes: false,
						enableClickSelection: true,
					},
				}}
			/>
		</>
	);
};
